import {getMyStuffPageHash} from '@Libraries/my-stuff/my-stuff-library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import type {ContactsContainerQuery} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {
  ContactsTabSelection,
  HAS_SEEN_CONTACTS_SIDEBAR_INTRO_POPUP,
  SIDEBAR_ACTIVITY_INDICATOR_TIME,
  SIGNUP_FORM_ITEM_INDICATOR_TIME,
  SmartMailingListTypes,
} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import type {MailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import type {AudienceCustomFieldData, RawAudience, UserCustomFieldMeta} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import {type RawAudienceFieldNames} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import {getSeparatedStringFromCamelCase, LetterCase} from '@Utils/string.util';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {type HasUserSeenData, isUserLoggedIn} from '@Libraries/user.library';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import type {AjaxResponse} from '@Utils/ajax.util';

export const isPageRelatedToMailingLists = (): boolean => {
  const pageHash = getMyStuffPageHash();
  return pageHash === MY_STUFF_HASHES.mailingLists;
};

export const isPageRelatedToSignUpForms = (): boolean => {
  const pageHash = getMyStuffPageHash();
  return pageHash === MY_STUFF_HASHES.signupForms;
};

export const showMailingListDeletionForbiddenDialog = (): void => {
  openModal(MODAL_IDS.ERROR_MODAL_ID, {
    message: window.i18next.t('pmwjs_mailing_list_deletion_forbidden'),
    log: false,
  });
};

export const isMailingListDeleteForbidden = (response: AjaxResponse): boolean => {
  return response.code === 'mailing-list-in-signup-form';
};

export const isPageRelatedToContacts = (): boolean => {
  return isPageRelatedToMailingLists() || isPageRelatedToSignUpForms();
};

export const isContactsExtraSmallScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isExtraSmallScreen;
};

export const isContactsSmallScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isSmallScreen;
};

export const isContactsMediumScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isMediumScreen;
};

export const isContactsLargeScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isLargeScreen;
};

export const isContactsExtraLargeScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isExtraLargeScreen;
};

export const isContactsTabSize = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return isContactsMediumScreen(params) || isContactsSmallScreen(params);
};

export const hidePaddedTopBarMyStuff = (): void => {
  document.querySelector('.js-padded-top-bar')?.classList.add('_hidden');
};

export const unhidePaddedTopBarMyStuff = (): void => {
  document.querySelector('.js-padded-top-bar')?.classList.remove('_hidden');
};

export const getTestMailingList = (): MailingList => {
  return {
    id: 1,
    name: 'Test Mailing List',
    createdOn: '',
    isSmart: false,
    isSpam: false,
  };
};

export const isMailingListsContactsTabSelected = (tabSelection: ContactsTabSelection): boolean => {
  return tabSelection === ContactsTabSelection.MAILING_LISTS;
};

export const isSignupFormsTabSelected = (tabSelection: ContactsTabSelection): boolean => {
  return tabSelection === ContactsTabSelection.SIGNUP_FORMS;
};

export const stringifyCookieValueForMailingListsSelection = (mailingListsSelected: MailingList): string => {
  return `${mailingListsSelected.id}`;
};

export const decodeCookieValueForMailingListsSelection = (value: string): number[] => {
  if (!value || typeof value !== 'string') return [];

  return value
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => !isNaN(id));
};

const sortUserCustomFields = (userCustomFieldsMeta: UserCustomFieldMeta[]): UserCustomFieldMeta[] => {
  return [...userCustomFieldsMeta].sort((a, b) => a.idUserHasCustomField - b.idUserHasCustomField);
};

export const stringifyRawAudience = (rawAudience: RawAudience, userCustomFieldsMeta: UserCustomFieldMeta[]): string => {
  const rawAudienceArray = [];

  rawAudienceArray.push(`"${rawAudience.email}"`);
  rawAudienceArray.push(`"${rawAudience.firstName ?? ''}"`);
  rawAudienceArray.push(`"${rawAudience.lastName ?? ''}"`);
  rawAudienceArray.push(`"${rawAudience.phoneNumber ?? ''}"`);
  rawAudienceArray.push(`"${rawAudience.address ?? ''}"`);
  rawAudienceArray.push(`"${String(rawAudience.birthday)}"`);

  const customFields = rawAudience.customFields;

  for (let i = 0; i < userCustomFieldsMeta.length; i++) {
    const customFieldId = userCustomFieldsMeta[i].idUserHasCustomField;
    if (!customFields?.[i]) {
      rawAudienceArray.push(`"${window.i18next.t('pmwjs_na')}"`);
    } else {
      const value = customFields[customFieldId];
      rawAudienceArray.push(`"${value}"`);
    }
  }

  return rawAudienceArray.join(',');
};

export const getCsvHeaders = (userCustomFieldsMeta: UserCustomFieldMeta[]): string[] => {
  const defaultFields: RawAudienceFieldNames[] = ['email', 'firstName', 'lastName', 'phoneNumber', 'address', 'birthday'];
  const sortedCustomFields = sortUserCustomFields(userCustomFieldsMeta);

  const customFieldNames = sortedCustomFields.map((customFieldMeta: UserCustomFieldMeta): string => {
    return customFieldMeta.label;
  });

  return [...(defaultFields as string[]), ...customFieldNames].map((fieldName) => {
    return getSeparatedStringFromCamelCase(fieldName, LetterCase.SENTENCE_CASE);
  });
};

export const getMailingListsDataForCsv = (mailingLists: MailingList, userCustomFieldsMeta: UserCustomFieldMeta[]): string[] => {
  const data: string[] = [];
  const sortedUserCustomFieldsMeta = sortUserCustomFields(userCustomFieldsMeta);

  const mailingListContacts = mailingLists.mailingListContacts;

  if (mailingListContacts !== undefined) {
    for (let j = 0; j < mailingListContacts.length; j++) {
      const string = stringifyRawAudience(mailingListContacts[j], sortedUserCustomFieldsMeta);
      data.push(string);
    }
  }

  return data;
};

export const getIndexForCustomFieldWithId = (idCustomField: number, userCustomFields: AudienceCustomFieldData): number => {
  const index = -1;
  const indices = Object.keys(userCustomFields);

  for (let i = 0; i < indices.length; i++) {
    const index = parseFloat(indices[i]);
    const data = userCustomFields[index];
    // @ts-ignore
    const idUserHasCustomField = data.idUserHasCustomField;

    if (idCustomField === idUserHasCustomField) {
      return i;
    }
  }

  return index;
};

export const onClickDownloadCsv = (mailingListToDownload: MailingList, userCustomFieldMeta: UserCustomFieldMeta[]): void => {
  if (!mailingListToDownload) {
    return;
  }

  const fileName = mailingListToDownload.name;
  const csvData = getMailingListsDataForCsv(mailingListToDownload, userCustomFieldMeta);
  const csvHeader = getCsvHeaders(userCustomFieldMeta);
  const csvDataToExport = [csvHeader.join(','), csvData.join('\n')].join('\n');

  const csvBlob = new Blob([csvDataToExport], {
    type: `data:text/csv;charset=utf-8`,
  });

  const encodedUri = URL.createObjectURL(csvBlob);
  const link = document.createElement('a');

  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
};

function openPublishDialog(id: string): void {
  window.PMW.formMaker.publishDialog.open(id);
}

export const openPublishEmailSignupFormDialog = (id: string) => {
  if (typeof window.PMW.formMaker === 'undefined') {
    window.PMW.getJsAndCssAssetGroup('formMakerDialog', openPublishDialog.bind(this, id));
  } else {
    openPublishDialog(id);
  }
};

export const hasUserSeenContactsSidebarIntroPopup = async (): Promise<boolean> => {
  const value = readCookie(HAS_SEEN_CONTACTS_SIDEBAR_INTRO_POPUP);
  if (value !== null) {
    return !!value;
  }

  if (!isUserLoggedIn()) {
    return false;
  }

  const data = (await window.PMW.writeLocal('emailmarketing/hasUserSeenContactsSidebarIntroPopup', {})) as HasUserSeenData;

  if ('hasUserSeen' in data) {
    return data.hasUserSeen;
  }

  return false;
};

export const markUserHasSeenContactsIntroPopup = (): void => {
  void window.PMW.writeLocal('emailmarketing/markUserSeenContactsSidebarIntroPopup');
};

export const getCurrentUTCDate = (): string => {
  const now = new Date();
  return new Date(now.getTime() + now.getTimezoneOffset() * 60000).toISOString();
};

export const getDateFromUTCValue = (stringifiedUTCDate: string): Date => {
  return new Date(stringifiedUTCDate);
};

export const setSidebarActivityIndicatorCookie = (): void => {
  const value = getCurrentUTCDate();
  setCookie(SIDEBAR_ACTIVITY_INDICATOR_TIME, value, 1);
};

export const getSidebarActivityIndicatorCookieValue = (): Date | null => {
  const value = readCookie(SIDEBAR_ACTIVITY_INDICATOR_TIME);

  if (!value) {
    return null;
  }

  return getDateFromUTCValue(value);
};

export const setSignupFormActivityIndicatorCookie = (): void => {
  const value = getCurrentUTCDate();
  setCookie(SIGNUP_FORM_ITEM_INDICATOR_TIME, value, 1);
};

export const getSignupFormActivityIndicatorCookie = (): Date | null => {
  const value = readCookie(SIGNUP_FORM_ITEM_INDICATOR_TIME);

  if (!value) {
    return null;
  }

  return getDateFromUTCValue(value);
};

export const fireSmartMailingListSelectionEvent = (smartMailingListType: SmartMailingListTypes): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_AUTO_MAILING_LIST, {
    [GA4EventParamName.AUTO_MAILING_LIST_TYPE]: smartMailingListType,
  });
};

export const handleSmartMailingListsGaEvents = (selectedMailingListIds: number[]): void => {
  const smartMailingListTypeInformation = window.PMW.redux.store.getState().mailingListContacts.smartMailingListsTooltipMap;

  if (!smartMailingListTypeInformation) {
    return;
  }

  for (let i = 0; i < selectedMailingListIds.length; i++) {
    const mailingListId = selectedMailingListIds[i];

    const isAllContactsMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.ALL_CONTACTS];
    const isLeastEngagedMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.LEAST_ENGAGED];
    const isMostEngagedMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.MOST_ENGAGED];
    const isSomewhatEngagedMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.SOMEWHAT_ENGAGED];
    const isEventAttendeesMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.EVENT_ATTENDEES];

    if (isAllContactsMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.ALL_CONTACTS);
    }

    if (isLeastEngagedMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.LEAST_ENGAGED);
    }

    if (isMostEngagedMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.MOST_ENGAGED);
    }

    if (isSomewhatEngagedMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.SOMEWHAT_ENGAGED);
    }

    if (isEventAttendeesMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.EVENT_ATTENDEES);
    }
  }
};

export const getInternationalisedLabel = (fieldName: RawAudienceFieldNames): string => {
  if (fieldName === 'email') {
    return window.i18next.t('pmwjs_email');
  }

  if (fieldName === 'firstName') {
    return window.i18next.t('pmwjs_first_name');
  }

  if (fieldName === 'lastName') {
    return window.i18next.t('pmwjs_last_name');
  }

  if (fieldName === 'phoneNumber') {
    return window.i18next.t('pmwjs_phone_number');
  }

  if (fieldName === 'address') {
    return window.i18next.t('pmwjs_address');
  }

  if (fieldName === 'birthday') {
    return window.i18next.t('pmwjs_birthday');
  }

  return '';
};
